import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { partnerChannels } from "@/mocks/partners";
import { computeDashboardAnalytics, type MessageRow, type ChannelConfigRow } from "./analytics";
import { getSession, getPartnerChannels } from "@/utils/auth";
import { supabase } from "@/utils/supabase/client";
import { getDashboardMetrics, DashboardMetrics } from "@/utils/dashboardData";
import ChatReport from "./components/ChatReport";
import SupportRequests from "./components/SupportRequests";
import LiveChat from "./components/LiveChat";
import opsConnectWidgetSource from "../../../sdk/react-native/src/OPSConnectWidget.tsx?raw";

type TestState = "idle" | "testing" | "success" | "error" | "unverified";
type BulkTestEntry = { channelId: string; status: TestState };

// Only Telegram has a stored per-partner credential (telegram_bot_token /
// telegram_chat_id) that lets us actually send a test message and confirm
// delivery. Other channels are provisioned at the platform/webhook level —
// there's no per-partner API key to test against yet, so we're honest about
// that instead of faking a pass/fail.
const REAL_TEST_CHANNELS = new Set(["telegram"]);

const channelIcons: Record<string, string> = {
  whatsapp: "ri-whatsapp-line", telegram: "ri-telegram-line", messenger: "ri-messenger-line",
  instagram: "ri-instagram-line", line: "ri-line-line", email: "ri-mail-line",
  livechat: "ri-chat-3-line", wechat: "ri-wechat-line",
};
const channelColors: Record<string, string> = {
  whatsapp: "#25D366", telegram: "#26A5E4", messenger: "#0084FF",
  instagram: "#E4405F", line: "#00C300", email: "#EA4335",
  livechat: "#1E7FC2", wechat: "#07C160",
};

function timeAgo(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

// Groups the dashboard's many sections into focused tabs instead of one
// long scroll: at-a-glance stats/monitoring, connecting/reporting on
// channels, the actual message inbox, and one-time setup/configuration.
const DASHBOARD_TABS: { id: "overview" | "channels" | "inbox" | "setup"; label: string; icon: string }[] = [
  { id: "overview", label: "Overview", icon: "ri-dashboard-line" },
  { id: "channels", label: "Channels", icon: "ri-plug-line" },
  { id: "inbox", label: "Inbox", icon: "ri-inbox-line" },
  { id: "setup", label: "Setup", icon: "ri-settings-3-line" },
];

export default function Dashboard() {
  const [configuredChannels, setConfiguredChannels] = useState<Set<string>>(new Set());
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [disconnectModal, setDisconnectModal] = useState<string | null>(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [activeAnalyticsChannel, setActiveAnalyticsChannel] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "channels" | "inbox" | "setup">("overview");
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [partnerIdState, setPartnerIdState] = useState<string | null>(null);
  const [partnerDbId, setPartnerDbId] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportModal, setReportModal] = useState<{ channelId: string; report: string } | null>(null);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [widgetName, setWidgetName] = useState("");
  const [widgetAvatar, setWidgetAvatar] = useState("");
  const [widgetColorFrom, setWidgetColorFrom] = useState("#24396D");
  const [widgetColorTo, setWidgetColorTo] = useState("#38BDEB");
  const [widgetLogo, setWidgetLogo] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [widgetContacts, setWidgetContacts] = useState<Record<string, string>>({});
  const [widgetCopied, setWidgetCopied] = useState(false);
  const [widgetRnCopied, setWidgetRnCopied] = useState(false);
  const [widgetSaving, setWidgetSaving] = useState(false);
  const [widgetSaved, setWidgetSaved] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [aiContextSaving, setAiContextSaving] = useState(false);
  const [aiContextSaved, setAiContextSaved] = useState(false);
  const [tgBotToken, setTgBotToken] = useState("");
  const [tgChatId, setTgChatId] = useState("");
  const [tgFinding, setTgFinding] = useState(false);
  const [tgSaving, setTgSaving] = useState(false);
  const [tgSaved, setTgSaved] = useState(false);
  const [liveMessages, setLiveMessages] = useState<MessageRow[]>([]);
  const [channelConfigRows, setChannelConfigRows] = useState<ChannelConfigRow[]>([]);
  const [testStates, setTestStates] = useState<Record<string, TestState>>({});
  const [bulkTest, setBulkTest] = useState<{ running: boolean; entries: BulkTestEntry[] }>({ running: false, entries: [] });

  useEffect(() => {
    getSession().then(async (session) => {
      if (session?.role === "partner" && session.partnerId) {
        setPartnerName(session.partnerName || null);
        setPartnerIdState(session.partnerId);
        setPartnerDbId(session.partnerDbId || null);
        // Load AI context + widget settings
        if (session.partnerDbId) {
          const { data: pd } = await supabase
            .from("partners")
            .select("ai_business_context, widget_settings, telegram_bot_token, telegram_chat_id")
            .eq("id", session.partnerDbId)
            .maybeSingle();
          if (pd?.ai_business_context) setAiContext(pd.ai_business_context as string);
          if (pd?.telegram_bot_token) setTgBotToken(pd.telegram_bot_token as string);
          if (pd?.telegram_chat_id) setTgChatId(pd.telegram_chat_id as string);
          if (pd?.widget_settings) {
            const ws = pd.widget_settings as Record<string, unknown>;
            if (ws.name)      setWidgetName(ws.name as string);
            if (ws.avatar)    setWidgetAvatar(ws.avatar as string);
            if (ws.logo)      setWidgetLogo(ws.logo as string);
            if (ws.colorFrom) setWidgetColorFrom(ws.colorFrom as string);
            if (ws.colorTo)   setWidgetColorTo(ws.colorTo as string);
            if (ws.contacts)  setWidgetContacts(ws.contacts as Record<string, string>);
          }
        }
        const name = session.partnerName || "";
        if (name) {
          setWidgetName(name);
          const initials = name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
          setWidgetAvatar(initials.length >= 2 ? initials : name.slice(0, 2).toUpperCase());
        }
        // Load channels from Supabase (falls back to localStorage if needed)
        const channels = await getPartnerChannels(session.partnerId);
        setConfiguredChannels(new Set(channels));

        if (session.partnerDbId) {
          try {
            const m = await getDashboardMetrics(session.partnerDbId, session.partnerId, channels);
            setMetrics(m);
          } catch (e) { console.error("Failed to load dashboard metrics:", e); }
        }
        setMetricsLoading(false);
      } else {
        setMetricsLoading(false);
        // Fallback: localStorage (covers channels added via wizard before Supabase)
        const ids = new Set<string>();
        if (localStorage.getItem("omni_wa_configured") === "true") ids.add("whatsapp");
        if (localStorage.getItem("omni_ig_configured") === "true") ids.add("instagram");
        if (localStorage.getItem("omni_line_configured") === "true") ids.add("line");
        if (localStorage.getItem("omni_email_configured") === "true") ids.add("email");
        if (localStorage.getItem("omni_lc_configured") === "true") ids.add("livechat");
        if (localStorage.getItem("omni_wc_configured") === "true") ids.add("wechat");
        const savedCompleted = localStorage.getItem("omni_completed_setups");
        if (savedCompleted) {
          try { const c = JSON.parse(savedCompleted); Object.keys(c).forEach((k) => { if (c[k]) ids.add(k); }); } catch { /* ignore */ }
        }
        const savedKeys = localStorage.getItem("omni_saved_keys");
        if (savedKeys) {
          try { const keys = JSON.parse(savedKeys); Object.keys(keys).forEach((k) => { if (keys[k]) ids.add(k); }); } catch { /* ignore */ }
        }
        setConfiguredChannels(ids);
        const name = localStorage.getItem("omni_partner_name") || "";
        setPartnerName(name || null);
        setPartnerIdState(localStorage.getItem("omni_partner_id"));
        if (name) {
          setWidgetName(name);
          const initials = name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
          setWidgetAvatar(initials.length >= 2 ? initials : name.slice(0, 2).toUpperCase());
        }
      }
    });
  }, []);

  // Live data for stat tiles, weekly chart, monthly reports, and the
  // activity feed — replaces the old hardcoded mock/dashboard.ts numbers.
  useEffect(() => {
    if (!partnerDbId) return;
    (async () => {
      const [{ data: msgs }, { data: configs }] = await Promise.all([
        supabase
          .from("messages")
          .select("channel, direction, sender_id, sender_name, status, created_at")
          .eq("partner_id", partnerDbId),
        partnerIdState
          ? supabase.from("channel_configs").select("channel_id, created_at").eq("partner_id", partnerIdState)
          : Promise.resolve({ data: [] as ChannelConfigRow[] }),
      ]);
      setLiveMessages((msgs as MessageRow[]) ?? []);
      setChannelConfigRows((configs as ChannelConfigRow[]) ?? []);
    })();
  }, [partnerDbId, partnerIdState]);

  const showToast = (msg: string) => { setToastMessage(msg); setTimeout(() => setToastMessage(null), 2500); };

  const extractColorsFromLogo = async (imgUrl: string): Promise<{ from: string; to: string } | null> => {
    const toHex = (r: number, g: number, b: number) =>
      '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');

    const analysePixels = (data: Uint8ClampedArray): { from: string; to: string } | null => {
      const buckets: Record<string, { r: number; g: number; b: number; count: number }> = {};
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        if (a < 128) continue;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const sat = max === 0 ? 0 : (max - min) / max;
        const lit = (max + min) / 510;
        if (sat < 0.18 || lit > 0.90 || lit < 0.07) continue;
        const br = Math.round(r / 32) * 32, bg = Math.round(g / 32) * 32, bb = Math.round(b / 32) * 32;
        const key = `${br},${bg},${bb}`;
        if (!buckets[key]) buckets[key] = { r: br, g: bg, b: bb, count: 0 };
        buckets[key].count++;
      }
      const sorted = Object.values(buckets).sort((a, b) => b.count - a.count);
      if (sorted.length === 0) return null;
      const top = sorted[0];
      return {
        to: toHex(top.r, top.g, top.b),
        from: toHex(Math.round(top.r * 0.55), Math.round(top.g * 0.55), Math.round(top.b * 0.55)),
      };
    };

    try {
      const res = await fetch(imgUrl, { mode: 'cors', cache: 'no-cache' });
      if (!res.ok) return null;
      const blobUrl = URL.createObjectURL(await res.blob());
      return new Promise<{ from: string; to: string } | null>((resolve) => {
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(blobUrl);
          try {
            const SIZE = 80;
            const canvas = document.createElement('canvas');
            canvas.width = SIZE; canvas.height = SIZE;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(null); return; }
            ctx.drawImage(img, 0, 0, SIZE, SIZE);
            resolve(analysePixels(ctx.getImageData(0, 0, SIZE, SIZE).data));
          } catch { resolve(null); }
        };
        img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(null); };
        img.src = blobUrl;
      });
    } catch {
      return null;
    }
  };

  const handleExtractColors = async () => {
    if (!widgetLogo) return;
    const result = await extractColorsFromLogo(widgetLogo);
    if (result) {
      setWidgetColorFrom(result.from);
      setWidgetColorTo(result.to);
      showToast('Colors extracted from logo!');
    } else {
      showToast('Could not read colors — try a logo hosted on your own domain or uploaded via the Upload button.');
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !partnerIdState) return;
    setLogoUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${partnerIdState}/logo.${ext}`;
    const { data, error } = await supabase.storage
      .from('partner-logos')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (error) {
      showToast('Upload failed: ' + error.message);
    } else {
      const { data: urlData } = supabase.storage.from('partner-logos').getPublicUrl(data.path);
      setWidgetLogo(urlData.publicUrl);
      showToast('Logo uploaded!');
    }
    setLogoUploading(false);
    e.target.value = '';
  };

  const saveWidgetSettings = async () => {
    if (!partnerDbId) return;
    setWidgetSaving(true);
    const { error } = await supabase
      .from("partners")
      .update({
        widget_settings: {
          name: widgetName,
          avatar: widgetAvatar,
          logo: widgetLogo,
          colorFrom: widgetColorFrom,
          colorTo: widgetColorTo,
          contacts: widgetContacts,
        },
      })
      .eq("id", partnerDbId);
    setWidgetSaving(false);
    if (!error) {
      setWidgetSaved(true);
      setTimeout(() => setWidgetSaved(false), 2500);
      showToast("Widget settings saved!");
    } else {
      showToast("Error saving widget settings.");
    }
  };

  const saveAiContext = async () => {
    if (!partnerDbId) return;
    setAiContextSaving(true);
    const { error } = await supabase
      .from("partners")
      .update({ ai_business_context: aiContext })
      .eq("id", partnerDbId);
    setAiContextSaving(false);
    if (!error) {
      setAiContextSaved(true);
      setTimeout(() => setAiContextSaved(false), 2500);
      showToast("AI context saved!");
    } else {
      showToast("Error saving AI context.");
    }
  };

  const findTgChatId = async () => {
    if (!tgBotToken.trim()) { showToast("Enter your Bot Token first."); return; }
    setTgFinding(true);
    try {
      const res = await fetch(`https://api.telegram.org/bot${tgBotToken.trim()}/getUpdates`);
      const json = await res.json() as { ok: boolean; result: { message?: { chat: { id: number } } }[] };
      if (!json.ok || json.result.length === 0) {
        showToast("No messages found. Send any message to your bot first, then try again.");
      } else {
        const chatId = String(json.result[json.result.length - 1].message?.chat.id ?? "");
        if (chatId) { setTgChatId(chatId); showToast("Chat ID found: " + chatId); }
        else showToast("Could not read Chat ID. Make sure you messaged your bot.");
      }
    } catch { showToast("Failed to contact Telegram. Check your bot token."); }
    setTgFinding(false);
  };

  const saveTgChatId = async () => {
    if (!partnerDbId) return;
    setTgSaving(true);
    const { error } = await supabase
      .from("partners")
      .update({ telegram_bot_token: tgBotToken || null, telegram_chat_id: tgChatId || null })
      .eq("id", partnerDbId);
    setTgSaving(false);
    if (!error) {
      setTgSaved(true);
      setTimeout(() => setTgSaved(false), 2500);
      showToast("Telegram alert saved!");
    } else {
      showToast("Error saving Telegram alert.");
    }
  };

  const WIDGET_CHANNEL_FIELDS: Record<string, { label: string; placeholder: string; hint: string; icon: string; color: string }> = {
    messenger:  { label: "Messenger",  placeholder: "m.me/YourPage",             hint: "Page username or m.me/ link",    icon: "ri-messenger-line",  color: "#0084FF" },
    whatsapp:   { label: "WhatsApp",   placeholder: "46707383361",                hint: "Phone number with country code", icon: "ri-whatsapp-line",   color: "#25D366" },
    telegram:   { label: "Telegram",   placeholder: "t.me/yourusername",          hint: "Username, @handle, or t.me/ link", icon: "ri-telegram-line",   color: "#26A5E4" },
    line:       { label: "LINE",       placeholder: "your-line-id",               hint: "LINE ID",                    icon: "ri-line-line",        color: "#00C300" },
    instagram:  { label: "Instagram",  placeholder: "yourusername",               hint: "Instagram username (without @)", icon: "ri-instagram-line",  color: "#E4405F" },
    email:      { label: "Email",      placeholder: "hello@yourbusiness.com",     hint: "Support email address",      icon: "ri-mail-line",        color: "#EA4335" },
  };

  const generateEmbedCode = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://opsconnect.io";
    const supabaseApiBase = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) + "/functions/v1";
    const lines: string[] = [`  src="${origin}/widget.js"`];
    if (partnerIdState)                       lines.push(`  data-partner-id="${partnerIdState}"`);
    if (widgetName)                           lines.push(`  data-name="${widgetName}"`);
    if (widgetLogo)                           lines.push(`  data-logo="${widgetLogo}"`);
    if (widgetAvatar)                         lines.push(`  data-avatar="${widgetAvatar}"`);
    if (widgetColorFrom !== "#24396D")        lines.push(`  data-color-from="${widgetColorFrom}"`);
    if (widgetColorTo   !== "#38BDEB")        lines.push(`  data-color-to="${widgetColorTo}"`);
    if (supabaseApiBase && !supabaseApiBase.startsWith("undefined")) lines.push(`  data-api="${supabaseApiBase}"`);
    if (widgetContacts.messenger)             lines.push(`  data-messenger="${widgetContacts.messenger}"`);
    if (widgetContacts.whatsapp)              lines.push(`  data-whatsapp="${widgetContacts.whatsapp}"`);
    if (widgetContacts.telegram)             lines.push(`  data-telegram="${widgetContacts.telegram}"`);
    if (widgetContacts.line)                  lines.push(`  data-line="${widgetContacts.line}"`);
    if (widgetContacts.instagram)             lines.push(`  data-instagram="${widgetContacts.instagram}"`);
    if (widgetContacts.email)                 lines.push(`  data-email="${widgetContacts.email}"`);
    return `<script\n${lines.join("\n")}>\n</script>`;
  };

  const generateReactNativeSnippet = () => {
    const supabaseApiBase = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) + "/functions/v1";
    const apiBase = supabaseApiBase && !supabaseApiBase.startsWith("undefined") ? supabaseApiBase : "https://your-project.supabase.co/functions/v1";
    const pid = partnerIdState || "<your-partner-id>";
    const lines: string[] = [];
    lines.push("// 1. Install: npm install react-native-webview");
    lines.push("// 2. Save the downloaded file as OPSConnectWidget.tsx in your");
    lines.push("//    project root — the same folder as App.tsx");
    lines.push("// 3. Use the component:");
    lines.push("");
    lines.push("import React, { useState } from 'react';");
    lines.push("import { Modal, TouchableOpacity, Text } from 'react-native';");
    lines.push("import { OPSConnectWidget } from './OPSConnectWidget';");
    lines.push("");
    lines.push("export default function App() {");
    lines.push("  const [chatOpen, setChatOpen] = useState(false);");
    lines.push("  return (");
    lines.push("    <>")
    lines.push("      <TouchableOpacity onPress={() => setChatOpen(true)}>");
    lines.push("        <Text>Chat with us</Text>");
    lines.push("      </TouchableOpacity>");
    lines.push("");
    lines.push("      <Modal visible={chatOpen} animationType=\"slide\" onRequestClose={() => setChatOpen(false)}>");
    lines.push("        <OPSConnectWidget");
    lines.push(`          partnerId="${pid}"`);
    lines.push(`          api="${apiBase}"`);
    if (widgetName)      lines.push(`          name="${widgetName}"`);
    if (widgetLogo)      lines.push(`          logo="${widgetLogo}"`);
    if (widgetAvatar)    lines.push(`          avatar="${widgetAvatar}"`);
    if (widgetColorFrom !== "#24396D") lines.push(`          colorFrom="${widgetColorFrom}"`);
    if (widgetColorTo   !== "#38BDEB") lines.push(`          colorTo="${widgetColorTo}"`);
    if (widgetContacts.whatsapp)       lines.push(`          whatsapp="${widgetContacts.whatsapp}"`);
    if (widgetContacts.telegram)       lines.push(`          telegram="${widgetContacts.telegram}"`);
    if (widgetContacts.messenger)      lines.push(`          messenger="${widgetContacts.messenger}"`);
    if (widgetContacts.instagram)      lines.push(`          instagram="${widgetContacts.instagram}"`);
    if (widgetContacts.email)          lines.push(`          email="${widgetContacts.email}"`);
    lines.push("          onClose={() => setChatOpen(false)}");
    lines.push("        />");
    lines.push("      </Modal>");
    lines.push("    </>");
    lines.push("  );");
    lines.push("}");
    return lines.join("\n");
  };

  const handleCopyEmbed = async () => {
    const code = generateEmbedCode();
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Fallback for HTTP (non-secure) contexts
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setWidgetCopied(true);
    setTimeout(() => setWidgetCopied(false), 2000);
  };

  const handleCopyReactNativeScript = async () => {
    const code = generateReactNativeSnippet();
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setWidgetRnCopied(true);
    setTimeout(() => setWidgetRnCopied(false), 2000);
  };

  const handleDownloadReactNativeWidget = () => {
    const blob = new Blob([opsConnectWidgetSource], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "OPSConnectWidget.tsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Sends a real Telegram message via the partner's saved bot token and
  // records it in `messages`. Returns whether it actually succeeded.
  const testTelegramChannel = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!partnerDbId) return { ok: false, error: "Partner not loaded yet." };
    const { data, error } = await supabase.functions.invoke("test-telegram-channel", {
      body: { partner_id: partnerDbId },
    });
    if (error) return { ok: false, error: error.message };
    return data as { ok: boolean; error?: string };
  }, [partnerDbId]);

  const handleTestConnection = async (channelId: string) => {
    const channel = partnerChannels.find((ch) => ch.id === channelId);
    setTestStates((prev) => ({ ...prev, [channelId]: "testing" }));

    if (REAL_TEST_CHANNELS.has(channelId)) {
      const result = await testTelegramChannel();
      setTestStates((prev) => ({ ...prev, [channelId]: result.ok ? "success" : "error" }));
      showToast(
        result.ok
          ? `Test message sent through ${channel?.name || "channel"} successfully!`
          : (result.error || `Test failed for ${channel?.name || "channel"}.`)
      );
    } else {
      // No stored credential to test against for this channel yet — say so
      // honestly instead of faking a result.
      setTestStates((prev) => ({ ...prev, [channelId]: "unverified" }));
      showToast(`Automatic testing isn't available for ${channel?.name || "this channel"} yet — send a real message to confirm it's working.`);
    }
    setTimeout(() => { setTestStates((prev) => { const next = { ...prev }; delete next[channelId]; return next; }); }, 3000);
  };

  const handleBulkTestAll = useCallback(() => {
    const connected = partnerChannels.filter((ch) => configuredChannels.has(ch.id));
    if (connected.length === 0) { showToast("No connected channels to test."); return; }
    const entries: BulkTestEntry[] = connected.map((ch) => ({ channelId: ch.id, status: "idle" as TestState }));
    setBulkTest({ running: true, entries });

    const runSequential = async () => {
      let verified = 0;
      let unverified = 0;
      for (let i = 0; i < entries.length; i++) {
        setBulkTest((prev) => {
          const updated = [...prev.entries];
          updated[i] = { ...updated[i], status: "testing" };
          return { ...prev, entries: updated };
        });

        let status: TestState;
        if (REAL_TEST_CHANNELS.has(entries[i].channelId)) {
          const result = await testTelegramChannel();
          status = result.ok ? "success" : "error";
          if (result.ok) verified++;
        } else {
          status = "unverified";
          unverified++;
        }

        setBulkTest((prev) => {
          const updated = [...prev.entries];
          updated[i] = { ...updated[i], status };
          return { ...prev, entries: updated };
        });
      }
      setBulkTest((prev) => ({ ...prev, running: false }));
      showToast(
        `Bulk test complete: ${verified}/${entries.length} verified` +
        (unverified > 0 ? `, ${unverified} need manual verification` : "") + "."
      );
    };
    runSequential();
  }, [configuredChannels, testTelegramChannel]);

  const generateReport = (channelId: string): string => {
    const data = metrics?.monthlyReport[channelId];
    const channel = partnerChannels.find((c) => c.id === channelId);
    if (!data || !channel) return "";

    const date = new Date();
    const monthName = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    return `=========================================
MONTHLY REPORT — ${channel.name}
${monthName} ${year}
=========================================

SUMMARY
-----------------------------------------
Total Messages:    ${data.totalMessages.toLocaleString()}
vs Last Month:     ${data.vsLastMonthPct === null ? "N/A" : `${data.vsLastMonthPct >= 0 ? "+" : ""}${data.vsLastMonthPct.toFixed(1)}%`}
Peak Hour:         ${data.peakHour}
Top Contact:       ${data.topContact}

CHANNEL DETAILS
-----------------------------------------
Type:              ${channel.name}
Connected Since:   ${metrics?.channelMetrics[channelId]?.connectedSince ? new Date(metrics.channelMetrics[channelId].connectedSince as string).toLocaleDateString() : "N/A"}
Messages Today:    ${metrics?.channelMetrics[channelId]?.messagesToday.toLocaleString() || "0"}
All-Time Messages: ${metrics?.channelMetrics[channelId]?.totalMessages.toLocaleString() || "0"}

=========================================
Generated by OPSConnect Analytics
${date.toISOString().split("T")[0]}
=========================================`;
  };

  const handleGenerateReport = (channelId: string) => {
    const report = generateReport(channelId);
    setReportModal({ channelId, report });
  };

  const handleExportCSV = (channelId: string) => {
    const data = metrics?.monthlyReport[channelId];
    const channel = partnerChannels.find((c) => c.id === channelId);
    if (!data || !channel) return;

    const csvRows = [
      "Metric,Value",
      `Channel,${channel.name}`,
      `Total Messages,${data.totalMessages}`,
      `vs Last Month,${data.vsLastMonthPct === null ? "N/A" : `${data.vsLastMonthPct.toFixed(1)}%`}`,
      `Peak Hour,${data.peakHour}`,
      `Top Contact,${data.topContact}`,
    ];
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `monthly-report-${channelId}-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(`Monthly report for ${channel.name} exported!`);
  };

  const handleDisconnect = async (channelId: string) => {
    const removals: Record<string, string[]> = {
      whatsapp: ["omni_wa_configured", "omni_wa_phone", "omni_wa_cc"],
      instagram: ["omni_ig_configured", "omni_ig_data"],
      line: ["omni_line_configured", "omni_line_data"],
      email: ["omni_email_configured", "omni_email_data"],
      livechat: ["omni_lc_configured", "omni_lc_data"],
      wechat: ["omni_wc_configured", "omni_wc_data"],
      telegram: ["omni_api_key_telegram"],
      messenger: ["omni_api_key_messenger"],
    };
    const keys = removals[channelId] || [];
    keys.forEach((k) => localStorage.removeItem(k));
    if (channelId === "telegram" || channelId === "messenger") {
      const savedKeys = localStorage.getItem("omni_saved_keys");
      if (savedKeys) {
        try { const parsed = JSON.parse(savedKeys); delete parsed[channelId]; localStorage.setItem("omni_saved_keys", JSON.stringify(parsed)); } catch { /* ignore */ }
      }
      const savedCompleted = localStorage.getItem("omni_completed_setups");
      if (savedCompleted) {
        try { const parsed = JSON.parse(savedCompleted); delete parsed[channelId]; localStorage.setItem("omni_completed_setups", JSON.stringify(parsed)); } catch { /* ignore */ }
      }
    }
    const channel = partnerChannels.find((ch) => ch.id === channelId);
    if (partnerIdState) {
      const { error } = await supabase
        .from("channel_configs")
        .delete()
        .eq("partner_id", partnerIdState)
        .eq("channel_id", channelId);
      if (error) {
        showToast(`Failed to disconnect ${channel?.name || "channel"}: ${error.message}`);
        return;
      }
    }
    setConfiguredChannels((prev) => { const next = new Set(prev); next.delete(channelId); return next; });
    setDisconnectModal(null);
    showToast(`${channel?.name || "Channel"} disconnected successfully.`);
  };

  const connectedArray = partnerChannels.filter((ch) => configuredChannels.has(ch.id));
  const disconnectedArray = partnerChannels.filter((ch) => !configuredChannels.has(ch.id));

  const connectedChannelIds = connectedArray.map((ch) => ch.id);
  const { stats: dashboardStats, channelMetrics, analyticsTrends, monthlyReportData, recentActivity } = useMemo(
    () => computeDashboardAnalytics(liveMessages, channelConfigRows, connectedChannelIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveMessages, channelConfigRows, configuredChannels]
  );

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24 min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(56,189,235,0.12),transparent_34%),linear-gradient(180deg,#FCFBF7_0%,#F7F4EC_100%)]">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">Channel Dashboard</h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {partnerName ? `${partnerName}'s unified inbox` : "Monitor and manage all your connected messaging channels"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/partners#connect"
                  className="text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-md inline-flex items-center gap-1.5"
                >
                  <i className="ri-add-line"></i> Connect New Channel
                </Link>
              </div>
            </div>

            {/* Partner ID Badge */}
            {partnerIdState && (
              <div className="bg-background-100 rounded-xl border border-accent-200/60 px-5 py-3 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-key-2-line text-sm text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-800">Partner ID</p>
                    <code className="text-xs font-mono text-foreground-600 select-all">{partnerIdState}</code>
                  </div>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(partnerIdState); showToast("Partner ID copied!"); }} className="text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer self-start sm:self-auto">
                  <i className="ri-file-copy-line mr-1"></i>Copy ID
                </button>
              </div>
            )}

            {/* Bulk Test Progress */}
            {bulkTest.running && (
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 mb-8">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">Bulk Test Progress</h3>
                <div className="space-y-2">
                  {bulkTest.entries.map((entry) => {
                    const channel = partnerChannels.find((c) => c.id === entry.channelId);
                    return (
                      <div key={entry.channelId} className="flex items-center gap-3 bg-background-50 rounded-lg px-4 py-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md" style={{ backgroundColor: (channel?.color || "#999") + "20" }}>
                          <i className={`${channel?.icon || "ri-question-line"} text-xs`} style={{ color: channel?.color || "#999" }}></i>
                        </div>
                        <span className="flex-1 text-xs font-medium text-foreground-800">{channel?.name || entry.channelId}</span>
                        <div className="flex items-center gap-2">
                          {entry.status === "idle" && <span className="text-xs text-foreground-400">Waiting...</span>}
                          {entry.status === "testing" && <span className="flex items-center gap-1.5 text-xs text-foreground-500"><span className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"/> Testing</span>}
                          {entry.status === "success" && <span className="flex items-center gap-1 text-xs text-accent-600"><i className="ri-checkbox-circle-line"></i> Passed</span>}
                          {entry.status === "error" && <span className="flex items-center gap-1 text-xs text-red-500"><i className="ri-close-circle-line"></i> Failed</span>}
                          {entry.status === "unverified" && <span className="flex items-center gap-1 text-xs text-amber-600"><i className="ri-question-line"></i> Manual only</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Section Tabs */}
            <div className="flex items-center gap-1.5 mb-6 border-b border-background-200/70 overflow-x-auto">
              {DASHBOARD_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap cursor-pointer px-4 py-2.5 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  <i className={tab.icon}></i> {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "overview" && (
            <>
            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {(metricsLoading ? [] : [
                {
                  label: "Connected Channels", icon: "ri-link",
                  value: String(connectedArray.length),
                  trend: `${disconnectedArray.length} available`,
                },
                {
                  label: "Messages Today", icon: "ri-message-3-line",
                  value: (metrics?.messagesToday ?? 0).toLocaleString(),
                  trend: metrics && metrics.messagesYesterday > 0
                    ? `${metrics.messagesToday >= metrics.messagesYesterday ? "+" : ""}${(((metrics.messagesToday - metrics.messagesYesterday) / metrics.messagesYesterday) * 100).toFixed(0)}% vs yesterday`
                    : "vs yesterday",
                },
                {
                  label: "Active Conversations", icon: "ri-chat-3-line",
                  value: String(metrics?.activeConversations ?? 0),
                  trend: `${metrics?.waitingConversations ?? 0} awaiting reply`,
                },
                {
                  label: "Support Resolution Rate", icon: "ri-thumb-up-line",
                  value: metrics && metrics.totalRequests > 0 ? `${((metrics.resolvedRequests / metrics.totalRequests) * 100).toFixed(1)}%` : "—",
                  trend: metrics ? `${metrics.resolvedRequests}/${metrics.totalRequests} resolved` : "No requests yet",
                },
              ]).map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className={`${stat.icon} text-base text-primary-600`}></i>
                    </div>
                    <span className="text-[10px] font-medium text-accent-600 bg-accent-100 px-1.5 py-0.5 rounded-full">{stat.trend}</span>
                  </div>
                  <div className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
              {metricsLoading && [0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-background-100 rounded-xl border border-background-200/70 p-5 h-[92px] animate-pulse" />
              ))}
            </div>
            </>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Connected Channels + Analytics */}
              <div className="lg:col-span-2 space-y-6">
                {activeTab === "overview" && (
                <>
                {/* Connected Channels */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg font-bold text-foreground-950">Connected Channels ({connectedArray.length})</h2>
                    <button
                      onClick={() => setAnalyticsOpen(!analyticsOpen)}
                      className="text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-bar-chart-line"></i> {analyticsOpen ? "Hide Analytics" : "View Analytics"}
                    </button>
                  </div>

                  {connectedArray.length === 0 ? (
                    <div className="bg-background-100 rounded-xl border border-background-200/70 p-8 text-center">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200/80 mb-4">
                        <i className="ri-plug-line text-2xl text-foreground-400"></i>
                      </div>
                      <p className="text-sm font-semibold text-foreground-700">No channels connected yet</p>
                      <p className="text-xs text-foreground-500 mt-1 mb-4">Connect your first messaging channel to get started.</p>
                      <Link to="/partners#connect" className="text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-md inline-block">
                        Set Up Channels
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {connectedArray.map((channel) => {
                        const chMetrics = metrics?.channelMetrics[channel.id];
                        const testState = testStates[channel.id];
                        return (
                          <div key={channel.id} className="bg-background-100 rounded-xl border border-accent-200/60 p-5">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 flex items-center justify-center rounded-xl" style={{ backgroundColor: channel.color + "20" }}>
                                  <i className={`${channel.icon} text-xl`} style={{ color: channel.color }}></i>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-heading text-sm font-semibold text-foreground-900">{channel.name}</h3>
                                    <span className="w-2 h-2 rounded-full bg-accent-500" />
                                  </div>
                                  <p className="text-xs text-foreground-500 mt-0.5">
                                    Connected since {chMetrics?.connectedSince ? new Date(chMetrics.connectedSince).toLocaleDateString() : "recently"}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => handleTestConnection(channel.id)}
                                  disabled={!!testState}
                                  className={`text-xs font-medium whitespace-nowrap cursor-pointer px-3 py-1.5 rounded-md transition-colors ${
                                    testState === "success"
                                      ? "bg-accent-100 text-accent-600"
                                      : testState === "error"
                                        ? "bg-red-100 text-red-600"
                                        : testState === "unverified"
                                          ? "bg-amber-100 text-amber-700"
                                          : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                                  }`}
                                >
                                  {testState === "testing" ? (
                                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin" /> Testing</span>
                                  ) : testState === "success" ? (
                                    <span className="flex items-center gap-1"><i className="ri-check-line"></i> Passed</span>
                                  ) : testState === "error" ? (
                                    <span className="flex items-center gap-1"><i className="ri-close-line"></i> Failed</span>
                                  ) : testState === "unverified" ? (
                                    <span className="flex items-center gap-1"><i className="ri-question-line"></i> Manual only</span>
                                  ) : (
                                    <span className="flex items-center gap-1"><i className="ri-send-plane-line text-[10px]"></i> Test</span>
                                  )}
                                </button>
                                <button onClick={() => setDisconnectModal(channel.id)} className="text-xs font-medium whitespace-nowrap cursor-pointer px-3 py-1.5 rounded-md text-foreground-500 hover:text-red-500 hover:bg-red-50 transition-colors">
                                  Disconnect
                                </button>
                              </div>
                            </div>
                            {chMetrics && (
                              <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="bg-background-50 rounded-lg p-3">
                                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">Today</p>
                                  <p className="text-sm font-bold text-foreground-900">{chMetrics.messagesToday.toLocaleString()}</p>
                                </div>
                                <div className="bg-background-50 rounded-lg p-3">
                                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">Total</p>
                                  <p className="text-sm font-bold text-foreground-900">{chMetrics.totalMessages.toLocaleString()}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Analytics Charts */}
                {analyticsOpen && connectedArray.length > 0 && (
                  <div>
                    <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">Channel Analytics — Weekly Message Volume</h2>
                    <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
                      {/* Channel selector tabs */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        <button
                          onClick={() => setActiveAnalyticsChannel(null)}
                          className={`text-xs font-medium whitespace-nowrap cursor-pointer px-3 py-1.5 rounded-full transition-colors ${activeAnalyticsChannel === null ? "bg-primary-500 text-background-50 dark:text-foreground-950" : "bg-background-200/70 text-foreground-600 hover:bg-background-300"}`}
                        >
                          All Channels
                        </button>
                        {connectedArray.map((ch) => (
                          <button
                            key={ch.id}
                            onClick={() => setActiveAnalyticsChannel(ch.id)}
                            className={`text-xs font-medium whitespace-nowrap cursor-pointer px-3 py-1.5 rounded-full transition-colors ${activeAnalyticsChannel === ch.id ? "bg-primary-500 text-background-50 dark:text-foreground-950" : "bg-background-200/70 text-foreground-600 hover:bg-background-300"}`}
                          >
                            {ch.name}
                          </button>
                        ))}
                      </div>

                      {/* Bar chart */}
                      <div className="relative">
                        <div className="flex items-end gap-2 md:gap-3 h-[200px] md:h-[240px] mb-3">
                          {(() => {
                            const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                            const dayTotal = (day: string) => connectedArray.reduce((sum, ch) => {
                              const trend = metrics?.weeklyTrends[ch.id];
                              const entry = trend?.find((d) => d.day === day);
                              return sum + (entry?.value || 0);
                            }, 0);
                            const maxTotal = Math.max(1, ...days.map(dayTotal));
                            return days.map((day) => {
                            const totalValue = dayTotal(day);
                            return (
                              <div key={day} className="flex-1 flex flex-col items-center gap-1.5">
                                <span className="text-[10px] font-medium text-foreground-500">{totalValue}</span>
                                <div className="w-full relative h-[180px] md:h-[210px] flex flex-col justify-end">
                                  {/* Stacked bars for each connected channel */}
                                  {connectedArray.map((ch, idx) => {
                                    const trend = metrics?.weeklyTrends[ch.id];
                                    const entry = trend?.find((d) => d.day === day);
                                    const chValue = entry?.value || 0;
                                    const chHeightPct = Math.max(chValue > 0 ? 2 : 0, (chValue / maxTotal) * 100);
                                    const totalBelow = connectedArray.slice(0, idx).reduce((sum, c) => {
                                      const t = metrics?.weeklyTrends[c.id];
                                      const e = t?.find((d) => d.day === day);
                                      return sum + (e?.value || 0);
                                    }, 0);
                                    return (
                                      <div
                                        key={ch.id}
                                        className="absolute left-0 right-0 rounded-t-sm transition-all duration-300"
                                        style={{
                                          height: `${chHeightPct}%`,
                                          bottom: `${(totalBelow / maxTotal) * 100}%`,
                                          backgroundColor: ch.color + (activeAnalyticsChannel && activeAnalyticsChannel !== ch.id ? "30" : "90"),
                                          zIndex: connectedArray.length - idx,
                                        }}
                                        title={`${ch.name}: ${chValue}`}
                                      />
                                    );
                                  })}
                                </div>
                                <span className="text-[10px] text-foreground-400">{day}</span>
                              </div>
                            );
                            });
                          })()}
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-background-200/50">
                        {connectedArray.map((ch) => (
                          <div key={ch.id} className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: ch.color }} />
                            <span className="text-[11px] text-foreground-600">{ch.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                </>
                )}

                {activeTab === "channels" && (
                <>
                {/* Available Channels */}
                {disconnectedArray.length > 0 && (
                  <div>
                    <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">Available Channels</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {disconnectedArray.map((channel) => (
                        <div key={channel.id} className="bg-background-100 rounded-xl border border-background-200/70 p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200/80">
                              <i className={`${channel.icon} text-lg`} style={{ color: channel.color }}></i>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground-800">{channel.name}</p>
                              <p className="text-[11px] text-foreground-400">Not connected</p>
                            </div>
                          </div>
                          <Link to={`/partners?channel=${channel.id}#connect`} className="text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                            Set up <i className="ri-arrow-right-line"></i>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Monthly Report Section */}
                {connectedArray.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-lg font-bold text-foreground-950">Monthly Reports</h2>
                      <button
                        onClick={() => setReportOpen(!reportOpen)}
                        className="text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-file-chart-line"></i> {reportOpen ? "Hide Reports" : "View Reports"}
                      </button>
                    </div>

                    {reportOpen && (
                      <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6 animate-[fadeInUp_0.3s_ease-out]">
                        <p className="text-xs text-foreground-500 mb-5">Download or view aggregated monthly summaries per channel. Data refreshed every 24 hours.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {connectedArray.map((channel) => {
                            const data = metrics?.monthlyReport[channel.id];
                            if (!data) return null;
                            return (
                              <div key={channel.id} className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                                <div className="flex items-center gap-2.5 mb-3">
                                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: channel.color + "20" }}>
                                    <i className={`${channel.icon} text-sm`} style={{ color: channel.color }}></i>
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-foreground-900">{channel.name}</p>
                                    <p className="text-[11px] text-foreground-400">{data.totalMessages.toLocaleString()} messages this month</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 mb-3">
                                  <div className="bg-background-100 rounded p-2">
                                    <p className="text-[10px] text-foreground-400">vs Last Month</p>
                                    <p className="text-xs font-bold text-foreground-800">{data.vsLastMonthPct === null ? "N/A" : `${data.vsLastMonthPct >= 0 ? "+" : ""}${data.vsLastMonthPct.toFixed(1)}%`}</p>
                                  </div>
                                  <div className="bg-background-100 rounded p-2">
                                    <p className="text-[10px] text-foreground-400">Peak Hour</p>
                                    <p className="text-xs font-bold text-foreground-800 truncate">{data.peakHour}</p>
                                  </div>
                                  <div className="bg-background-100 rounded p-2">
                                    <p className="text-[10px] text-foreground-400">Top Contact</p>
                                    <p className="text-xs font-bold text-foreground-800 truncate">{data.topContact}</p>
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleGenerateReport(channel.id)}
                                    className="flex-1 text-xs font-semibold bg-accent-500 text-background-50 dark:text-foreground-950 hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer px-3 py-2 rounded-md flex items-center justify-center gap-1"
                                  >
                                    <i className="ri-file-text-line text-[10px]"></i> View Report
                                  </button>
                                  <button
                                    onClick={() => handleExportCSV(channel.id)}
                                    className="flex-1 text-xs font-semibold bg-secondary-500 text-background-50 dark:text-foreground-950 hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer px-3 py-2 rounded-md flex items-center justify-center gap-1"
                                  >
                                    <i className="ri-download-line text-[10px]"></i> Export CSV
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                </>
                )}

                {activeTab === "inbox" && (
                <>
                {/* Support Requests — from the embedded chat widget */}
                <SupportRequests partnerId={partnerIdState} />

                {/* Chat Report */}
                <ChatReport partnerId={partnerDbId} partnerTextId={partnerIdState} />

                </>
                )}

                {activeTab === "setup" && (
                <>
                {/* AI Chat Setup */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h2 className="font-heading text-lg font-bold text-foreground-950">AI Chat Setup</h2>
                      <span className="text-[10px] font-semibold bg-primary-100 text-primary-600 px-2 py-0.5 rounded-full">Llama 3.3 70B</span>
                    </div>
                    <button
                      onClick={() => setAiOpen(!aiOpen)}
                      className="text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-sparkling-2-line"></i> {aiOpen ? "Hide Setup" : "Configure AI"}
                    </button>
                  </div>

                  {!aiOpen && (
                    <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                        <i className="ri-robot-2-line text-xl text-primary-600"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground-900">Power your chat widget with AI</p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {aiContext
                            ? `AI configured · ${aiContext.length} characters of business context`
                            : "Tell the AI about your business — hours, products, FAQs — so it answers visitors instantly."}
                        </p>
                      </div>
                      <button
                        onClick={() => setAiOpen(true)}
                        className="text-xs font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer px-4 py-2 rounded-md flex-shrink-0"
                      >
                        {aiContext ? "Edit" : "Set Up"}
                      </button>
                    </div>
                  )}

                  {aiOpen && (
                    <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6 space-y-5 animate-[fadeInUp_0.3s_ease-out]">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-1">Business Knowledge</p>
                        <p className="text-xs text-foreground-500 mb-3">
                          Write anything about your business the AI should know. The more detail, the better it answers.
                        </p>
                        <textarea
                          value={aiContext}
                          onChange={(e) => setAiContext(e.target.value)}
                          rows={10}
                          placeholder={`Example:\n\nBusiness: Ballangkmall\nType: Shopping mall\nLocation: Phnom Penh, Cambodia\nHours: Mon–Sun 10am–9pm\n\nShops: Nike, Adidas, Zara, H&M, Samsung, Apple\nFood: Food court floor 1, KFC, Pizza Hut, Coffee Bean\nParking: Free underground parking for 500 cars\n\nContact: +855 12 345 678 | info@ballangkmall.com\n\nFAQ:\n- ATMs? Yes, on every floor near elevators\n- Cinema? Yes, 6 screens on floor 4\n- Lost & found? Call security ext. 100`}
                          className="w-full bg-background-50 border border-background-200/70 rounded-xl px-4 py-3 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-300 resize-none leading-relaxed"
                        />
                        <p className="text-[11px] text-foreground-400 mt-2">
                          {aiContext.length} characters · Include hours, products, FAQs, location, and contact info.
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={saveAiContext}
                          disabled={aiContextSaving}
                          className={`text-sm font-semibold whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-md transition-colors flex items-center gap-2 ${
                            aiContextSaved
                              ? "bg-accent-500 text-background-50 dark:text-foreground-950"
                              : "bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600"
                          }`}
                        >
                          {aiContextSaving ? (
                            <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
                          ) : aiContextSaved ? (
                            <><i className="ri-checkbox-circle-line" /> Saved!</>
                          ) : (
                            <><i className="ri-save-line" /> Save Context</>
                          )}
                        </button>
                        {aiContext && (
                          <button onClick={() => setAiContext("")} className="text-xs font-medium text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
                            Clear
                          </button>
                        )}
                      </div>

                      <div className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                        <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                          <i className="ri-information-line text-primary-500"></i> How it works
                        </p>
                        <ul className="space-y-1.5 text-xs text-foreground-500">
                          <li>• Visitors ask questions in your widget → AI answers instantly using the context above</li>
                          <li>• When AI can't help, it collects the visitor's name + contact and notifies your team</li>
                          <li>• Escalated conversations appear in <strong className="text-foreground-700">Support Requests</strong> above</li>
                          <li>• Model: Llama 3.3 70B via Groq — smarter and free (up to 14,400 requests/day)</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Telegram Notifications */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h2 className="font-heading text-lg font-bold text-foreground-950">Live Chat Notifications</h2>
                      <p className="text-xs text-foreground-500 mt-0.5">Get a Telegram alert the moment a visitor requests live support</p>
                    </div>
                    <i className="ri-telegram-line text-2xl text-[#26A5E4]" />
                  </div>
                  <div className="bg-background-100 border border-background-200/70 rounded-2xl p-5 space-y-5">
                    {/* Step 1 */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-2">Step 1 — Create your bot</p>
                      <p className="text-xs text-foreground-500 mb-2">
                        Open Telegram → search <span className="font-semibold text-foreground-700">@BotFather</span> → send <code className="bg-background-200 px-1 rounded">/newbot</code> → follow the steps → copy the <b>Bot Token</b> it gives you.
                      </p>
                      <label className="text-xs font-medium text-foreground-600 block mb-1.5">Bot Token</label>
                      <input
                        type="text"
                        value={tgBotToken}
                        onChange={(e) => setTgBotToken(e.target.value)}
                        placeholder="e.g. 123456789:AAFxxxxxxxxxxxxxxxxxxxxxxxx"
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-300 font-mono"
                      />
                    </div>
                    {/* Step 2 */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-2">Step 2 — Get your Chat ID</p>
                      <p className="text-xs text-foreground-500 mb-2">
                        Open your new bot in Telegram → send any message (e.g. "hi") → then click <b>Find my Chat ID</b> below.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={tgChatId}
                          onChange={(e) => setTgChatId(e.target.value)}
                          placeholder="Auto-filled after clicking Find, or paste manually"
                          className="flex-1 bg-background-50 border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-300"
                        />
                        <button
                          onClick={findTgChatId}
                          disabled={tgFinding || !tgBotToken.trim()}
                          className="text-xs font-semibold px-3 py-2 rounded-lg border border-primary-400 text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                          {tgFinding ? <><span className="w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />Finding…</> : <><i className="ri-search-line" />Find my Chat ID</>}
                        </button>
                      </div>
                      <p className="text-[11px] text-foreground-400 mt-1.5">💡 For a group: add your bot to the group, send a message, then click Find.</p>
                    </div>
                    {/* Step 3 */}
                    <div className="flex items-center justify-between pt-1 border-t border-background-200/70">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-0.5">Step 3 — Save</p>
                        {tgBotToken && tgChatId && (
                          <p className="text-[11px] text-green-600 flex items-center gap-1"><i className="ri-checkbox-circle-line" /> Ready — alerts will fire on every live chat request</p>
                        )}
                      </div>
                      <button
                        onClick={saveTgChatId}
                        disabled={tgSaving || !tgBotToken.trim() || !tgChatId.trim()}
                        className={`text-xs font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                          tgSaved ? "bg-green-500 text-white" : "bg-primary-500 text-white hover:bg-primary-600"
                        }`}
                      >
                        {tgSaving ? "Saving…" : tgSaved ? "✓ Saved!" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Website Widget Generator */}
                {connectedArray.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-lg font-bold text-foreground-950">Website Widget</h2>
                      <button
                        onClick={() => setWidgetOpen(!widgetOpen)}
                        className="text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-code-s-slash-line"></i> {widgetOpen ? "Hide Generator" : "Get Embed Code"}
                      </button>
                    </div>

                    {!widgetOpen && (
                      <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 flex items-center gap-4">
                        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                          <i className="ri-chat-smile-2-line text-xl text-primary-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground-900">Add a chat widget to your website</p>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            Generate a single <code className="bg-background-200 px-1 rounded text-[11px]">&lt;script&gt;</code> tag — it captures visitor name, contact details, and what they want to ask about, then sends the request straight to your support dashboard on the partner website.
                          </p>
                        </div>
                        <button
                          onClick={() => setWidgetOpen(true)}
                          className="text-xs font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer px-4 py-2 rounded-md flex-shrink-0"
                        >
                          Generate
                        </button>
                      </div>
                    )}

                    {widgetOpen && (
                      <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6 space-y-6 animate-[fadeInUp_0.3s_ease-out]">

                        {/* Step 1: Branding */}
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3">1 — Widget Branding</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-foreground-600 block mb-1">Business name</label>
                              <input
                                type="text"
                                value={widgetName}
                                onChange={(e) => setWidgetName(e.target.value)}
                                placeholder="e.g. Lhvsbeauty"
                                className="w-full bg-background-50 border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-300"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-foreground-600 block mb-1">Avatar initials <span className="text-foreground-400 font-normal">(shown if no logo)</span></label>
                              <input
                                type="text"
                                value={widgetAvatar}
                                onChange={(e) => setWidgetAvatar(e.target.value.slice(0, 2).toUpperCase())}
                                placeholder="e.g. LB"
                                maxLength={2}
                                className="w-full bg-background-50 border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-300 uppercase"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="text-xs font-medium text-foreground-600 block mb-1">Logo <span className="text-foreground-400 font-normal">(replaces avatar — square PNG/SVG, max 2 MB)</span></label>
                              <div className="flex items-center gap-2">
                                {widgetLogo ? (
                                  <img src={widgetLogo} alt="logo" className="w-10 h-10 rounded-full object-cover border border-background-200/70 flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                ) : (
                                  <div className="w-10 h-10 rounded-full border-2 border-dashed border-background-300 flex items-center justify-center flex-shrink-0">
                                    <i className="ri-image-line text-foreground-300 text-base" />
                                  </div>
                                )}
                                <input
                                  type="url"
                                  value={widgetLogo}
                                  onChange={(e) => setWidgetLogo(e.target.value)}
                                  placeholder="Paste image URL, or upload →"
                                  className="flex-1 bg-background-50 border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 placeholder:text-foreground-300"
                                />
                                <label className={`flex-shrink-0 cursor-pointer flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${logoUploading ? 'opacity-50 pointer-events-none border-background-200 text-foreground-400' : 'border-primary-400 text-primary-600 hover:bg-primary-50'}`}>
                                  {logoUploading ? <><span className="w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" /> Uploading…</> : <><i className="ri-upload-2-line" /> Upload</>}
                                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
                                </label>
                                {widgetLogo && (
                                  <button onClick={() => setWidgetLogo("")} className="text-foreground-400 hover:text-foreground-700 flex-shrink-0 cursor-pointer" title="Remove logo">
                                    <i className="ri-close-line text-lg" />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-foreground-600 block mb-1">Color — from</label>
                              <div className="flex items-center gap-2">
                                <input type="color" value={widgetColorFrom} onChange={(e) => setWidgetColorFrom(e.target.value)} className="w-9 h-9 rounded-lg border border-background-200/70 cursor-pointer bg-background-50 p-0.5" />
                                <span className="text-xs font-mono text-foreground-600">{widgetColorFrom}</span>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-foreground-600 block mb-1">Color — to</label>
                              <div className="flex items-center gap-2">
                                <input type="color" value={widgetColorTo} onChange={(e) => setWidgetColorTo(e.target.value)} className="w-9 h-9 rounded-lg border border-background-200/70 cursor-pointer bg-background-50 p-0.5" />
                                <span className="text-xs font-mono text-foreground-600">{widgetColorTo}</span>
                              </div>
                            </div>
                            {widgetLogo && (
                              <div className="sm:col-span-2">
                                <button
                                  onClick={handleExtractColors}
                                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-primary-400 text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                                >
                                  <i className="ri-magic-line" /> Auto-detect colors from logo
                                </button>
                                <p className="text-[11px] text-foreground-400 mt-1">Extracts the dominant color from your logo and applies it to the gradient.</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Step 2: Channel contacts */}
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-1">2 — Channel Contact Details</p>
                          <p className="text-xs text-foreground-500 mb-3">Enter the public contact for each connected channel. Visitors will see buttons for the ones you fill in.</p>
                          <div className="space-y-2.5">
                            {connectedArray
                              .filter((ch) => WIDGET_CHANNEL_FIELDS[ch.id])
                              .map((ch) => {
                                const field = WIDGET_CHANNEL_FIELDS[ch.id];
                                return (
                                  <div key={ch.id} className="flex items-center gap-3 bg-background-50 rounded-lg border border-background-200/70 px-3 py-2.5">
                                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: field.color + "20" }}>
                                      <i className={`${field.icon} text-sm`} style={{ color: field.color }}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[11px] font-semibold text-foreground-700 mb-0.5">{field.label} <span className="font-normal text-foreground-400">— {field.hint}</span></p>
                                      <input
                                        type="text"
                                        value={widgetContacts[ch.id] || ""}
                                        onChange={(e) => setWidgetContacts((prev) => ({ ...prev, [ch.id]: e.target.value }))}
                                        placeholder={field.placeholder}
                                        className="w-full bg-transparent text-xs text-foreground-800 outline-none placeholder:text-foreground-300"
                                      />
                                    </div>
                                    {widgetContacts[ch.id] && (
                                      <i className="ri-checkbox-circle-line text-sm text-accent-500 flex-shrink-0"></i>
                                    )}
                                  </div>
                                );
                              })}
                            {connectedArray.filter((ch) => WIDGET_CHANNEL_FIELDS[ch.id]).length === 0 && (
                              <p className="text-xs text-foreground-400 italic">None of your connected channels (e.g. LiveChat, WeChat) expose a public link. Connect WhatsApp, Telegram, Messenger, LINE, Instagram, or Email to include channel buttons.</p>
                            )}
                          </div>
                        </div>

                        {/* Save settings */}
                        {partnerDbId && (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={saveWidgetSettings}
                              disabled={widgetSaving}
                              className={`text-sm font-semibold whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-md transition-colors flex items-center gap-2 ${
                                widgetSaved
                                  ? "bg-accent-500 text-background-50"
                                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
                              }`}
                            >
                              {widgetSaving ? (
                                <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
                              ) : widgetSaved ? (
                                <><i className="ri-checkbox-circle-line" /> Saved!</>
                              ) : (
                                <><i className="ri-save-line" /> Save Settings</>
                              )}
                            </button>
                            <p className="text-xs text-foreground-400">Settings are restored automatically next time you log in.</p>
                          </div>
                        )}

                        {/* Step 3: Generated code */}
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3">3 — Your Embed Code</p>
                          <div className="relative">
                            <pre className="bg-foreground-950 text-accent-300 text-[11px] font-mono rounded-xl p-4 overflow-x-auto leading-relaxed whitespace-pre">{generateEmbedCode()}</pre>
                            <button
                              onClick={handleCopyEmbed}
                              className={`absolute top-3 right-3 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                                widgetCopied
                                  ? "bg-accent-500 text-background-50 dark:text-foreground-950"
                                  : "bg-background-200/20 text-foreground-200 hover:bg-background-200/40"
                              }`}
                            >
                              <i className={widgetCopied ? "ri-checkbox-circle-line" : "ri-file-copy-line"}></i>
                              {widgetCopied ? "Copied!" : "Copy"}
                            </button>
                          </div>
                          <p className="text-xs text-foreground-400 mt-2.5">
                            Paste this just before <code className="bg-background-200 px-1 rounded text-[11px]">&lt;/body&gt;</code> on every page of your website.
                          </p>
                        </div>

                        {/* Step 4: React Native API client */}
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3">4 — React Native SDK</p>
                          <p className="text-xs text-foreground-500 mb-3">
                            Install <code className="bg-background-200 px-1 rounded text-[11px]">react-native-webview</code>, download{" "}
                            <code className="bg-background-200 px-1 rounded text-[11px]">OPSConnectWidget.tsx</code>{" "}
                            below and save it in your <b>project root — the same folder as App.tsx</b> (or your entry component), then use the snippet below it as-is.
                          </p>
                          <button
                            onClick={handleDownloadReactNativeWidget}
                            className="mb-3 text-xs font-semibold px-3 py-2 rounded-lg border border-primary-400 text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                          >
                            <i className="ri-download-2-line"></i> Download OPSConnectWidget.tsx
                          </button>
                          <div className="relative">
                            <pre className="bg-foreground-950 text-accent-300 text-[11px] font-mono rounded-xl p-4 overflow-x-auto leading-relaxed whitespace-pre max-h-72">{generateReactNativeSnippet()}</pre>
                            <button
                              onClick={handleCopyReactNativeScript}
                              className={`absolute top-3 right-3 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                                widgetRnCopied
                                  ? "bg-accent-500 text-background-50 dark:text-foreground-950"
                                  : "bg-background-200/20 text-foreground-200 hover:bg-background-200/40"
                              }`}
                            >
                              <i className={widgetRnCopied ? "ri-checkbox-circle-line" : "ri-file-copy-line"}></i>
                              {widgetRnCopied ? "Copied!" : "Copy"}
                            </button>
                          </div>
                          <p className="text-xs text-foreground-400 mt-2.5">
                            The widget opens in a full-screen Modal on iOS and Android with all your branding and AI settings pre-loaded.
                          </p>
                        </div>

                        {/* Mini preview */}
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3">Preview</p>
                          <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-lg overflow-hidden"
                                style={{ background: `linear-gradient(135deg, ${widgetColorFrom}, ${widgetColorTo})` }}
                              >
                                {widgetLogo
                                  ? <img src={widgetLogo} alt="logo" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                  : (widgetAvatar || "?")}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-foreground-900">{widgetName || "Your Business"}</p>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {connectedArray
                                    .filter((ch) => WIDGET_CHANNEL_FIELDS[ch.id] && widgetContacts[ch.id])
                                    .map((ch) => (
                                      <span
                                        key={ch.id}
                                        className="text-[10px] font-semibold text-white px-2 py-0.5 rounded-full"
                                        style={{ backgroundColor: WIDGET_CHANNEL_FIELDS[ch.id].color }}
                                      >
                                        {WIDGET_CHANNEL_FIELDS[ch.id].label}
                                      </span>
                                    ))}
                                  {connectedArray.filter((ch) => WIDGET_CHANNEL_FIELDS[ch.id] && widgetContacts[ch.id]).length === 0 && (
                                    <span className="text-[11px] text-foreground-400 italic">Fill in contacts above to see channel buttons</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow"
                              style={{ background: `linear-gradient(135deg, ${widgetColorFrom}, ${widgetColorTo})` }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.2L4 17.2V4h16v12z"/></svg>
                            </div>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                )}
                </>
                )}
              </div>

              {/* Right sidebar: Live Chat + Recent Activity */}
              <div className="space-y-5">
                {/* Live Chat — real-time agent replies to widget visitors */}
                <LiveChat partnerId={partnerIdState} />

                {/* Live Chat Performance — response time, resolution, CSAT */}
                {metrics?.liveChatPerf && (
                  <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                    <h2 className="font-heading text-sm font-bold text-foreground-950 mb-4">Live Chat Performance</h2>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-background-50 rounded-lg p-3">
                        <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">Total Chats</p>
                        <p className="text-sm font-bold text-foreground-900">{metrics.liveChatPerf.totalChats}</p>
                      </div>
                      <div className="bg-background-50 rounded-lg p-3">
                        <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">Closed</p>
                        <p className="text-sm font-bold text-foreground-900">
                          {metrics.liveChatPerf.totalChats > 0 ? `${((metrics.liveChatPerf.closedChats / metrics.liveChatPerf.totalChats) * 100).toFixed(0)}%` : "—"}
                        </p>
                      </div>
                      <div className="bg-background-50 rounded-lg p-3">
                        <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">Avg Response</p>
                        <p className="text-sm font-bold text-foreground-900">
                          {metrics.liveChatPerf.avgResponseMin == null ? "—" : `${metrics.liveChatPerf.avgResponseMin.toFixed(1)} min`}
                        </p>
                      </div>
                      <div className="bg-background-50 rounded-lg p-3">
                        <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">CSAT</p>
                        <p className="text-sm font-bold text-foreground-900">
                          {metrics.liveChatPerf.avgRating == null ? "No ratings yet" : `${metrics.liveChatPerf.avgRating.toFixed(1)} / 5`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Activity */}
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950 mb-4">Recent Activity</h2>
                  {!metrics || metrics.recentActivity.length === 0 ? (
                    <p className="text-xs text-foreground-400">No messages yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {metrics.recentActivity.map((activity, idx) => (
                        <div key={idx} className="flex gap-3 items-start">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: (channelColors[activity.channel] || "#999") + "20" }}>
                            <i className={`${channelIcons[activity.channel] || "ri-question-line"} text-xs`} style={{ color: channelColors[activity.channel] || "#999" }}></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-foreground-800 truncate">{activity.event}</p>
                            <p className="text-[11px] text-foreground-400 truncate">{activity.customer}</p>
                            <p className="text-[10px] text-foreground-300">{timeAgo(activity.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick Tips */}
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3">Quick Tips</h2>
                  <div className="space-y-3">
                    {[
                      { icon: "ri-lightbulb-line", text: "Connect at least 3 channels for the best omnichannel experience" },
                      { icon: "ri-shield-check-line", text: "All channels use end-to-end encryption by default" },
                      { icon: "ri-bar-chart-line", text: "Response times under 5 min boost CSAT by 40%" },
                    ].map((tip, i) => (
                      <div key={i} className="flex gap-2.5">
                        <i className={`${tip.icon} text-xs text-primary-500 mt-0.5 flex-shrink-0`}></i>
                        <p className="text-xs text-foreground-600 leading-relaxed">{tip.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Report Modal */}
      {reportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 max-w-lg w-full shadow-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-file-chart-line text-sm text-accent-600"></i>
                </div>
                <div>
                  <h3 className="font-heading text-sm font-bold text-foreground-950">Monthly Report</h3>
                  <p className="text-[11px] text-foreground-400">{partnerChannels.find((c) => c.id === reportModal.channelId)?.name}</p>
                </div>
              </div>
              <button onClick={() => setReportModal(null)} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <pre className="flex-1 overflow-auto bg-background-100 rounded-lg border border-background-200/70 p-4 text-xs font-mono text-foreground-700 whitespace-pre-wrap leading-relaxed">{reportModal.report}</pre>
            <div className="flex gap-3 mt-4 flex-shrink-0">
              <button
                onClick={() => { navigator.clipboard.writeText(reportModal.report); showToast("Report copied to clipboard!"); }}
                className="flex-1 text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer px-4 py-2.5 rounded-md border border-background-200/70 flex items-center justify-center gap-1.5"
              >
                <i className="ri-file-copy-line"></i> Copy
              </button>
              <button
                onClick={() => { handleExportCSV(reportModal.channelId); setReportModal(null); }}
                className="flex-1 text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer px-4 py-2.5 rounded-md flex items-center justify-center gap-1.5"
              >
                <i className="ri-download-line"></i> Download CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Modal */}
      {disconnectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 max-w-sm w-full shadow-lg">
            <div className="text-center mb-4">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-red-50 mb-3">
                <i className="ri-alert-line text-xl text-red-500"></i>
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-950">Disconnect Channel?</h3>
              <p className="text-xs text-foreground-500 mt-1">
                This will remove the {partnerChannels.find((c) => c.id === disconnectModal)?.name} configuration. You can reconnect at any time.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDisconnectModal(null)} className="flex-1 text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer px-4 py-2.5 rounded-md border border-background-200/70">
                Cancel
              </button>
              <button onClick={() => handleDisconnect(disconnectModal)} className="flex-1 text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer px-4 py-2.5 rounded-md">
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 animate-[fadeInUp_0.3s_ease-out]">
          <div className="flex items-center gap-2.5 bg-foreground-950 text-background-50 text-sm px-5 py-3 rounded-lg shadow-lg">
            <i className="ri-checkbox-circle-line text-accent-400"></i>
            {toastMessage}
          </div>
        </div>
      )}
    </>
  );
}
